import React, { useState, useEffect }  from 'react';
import IncidentStyle from '../styles/pages/IncidentDetailPage.module.scss'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom';
import { useGetIncidentByIdQuery, useUpdateIncidentMutation } from '../services/IncidentAPI';
import Loader from '../components/layout/Loader';
import { RootState } from '../store/GlobalStore'
import { SingleIncident, IncidentUpdateFormData } from '../utils/Interface'
import PageHeader from '../components/layout/PageHeader'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next';
const IncidentDetailPage: React.FC = () => {
    const { t, i18n  } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { register, handleSubmit, setValue, watch } = useForm<IncidentUpdateFormData>()
    const {
        data: incidentData,
        error: incidentError,
        isLoading: incidentLoading,
        refetch 
    } = useGetIncidentByIdQuery(Number(id))

    const [updateIncident, { isLoading, isError, isSuccess }] = useUpdateIncidentMutation()
    const [localResolved, setLocalResolved] = useState<boolean>(false);
    const authState = useSelector((state: RootState) => state.Auth)
    const navigate = useNavigate()


    const typeMapping: Record<string, string> = {
        dysfonction: t('dysfonction'),
        serviceRoom: t('serviceRoom'),
        housingIncident: t('housingIncident'),
        noSheet : t('noSheet'),
        noElectricity : t('noElectricity'),
        noWater : t('noWater'),
        noLight : t('noLight'),
        waterLeak : t('waterLeak'),
        other : t('other')
    }

    useEffect(() => {
        refetch(); // <-- Refetch la donnée à chaque chargement ou modification de l'ID
    }, [id, refetch]);

    // Définir explicitement le type de l'état local
    const [IncidentDetails, setIncidentDetails] = useState<SingleIncident>({
        id: 0,
        category: '',
        user: 0,
        title: '',
        description: '',
        date: '',
        resolved: false
    });
    // Mettre à jour l'état local dès que animationData est disponible
    useEffect(() => {
        if (incidentData) {
            if(authState?.user?.id !== incidentData.user){
                navigate('/mon-compte')
            }
            else{
                console.log(incidentData)
                setValue('id', incidentData.id)
                setValue('resolved', incidentData.resolved)
                setIncidentDetails({
                    id: incidentData.id,
                    category: incidentData.category,
                    user: incidentData.user,
                    title: incidentData.title,
                    description: incidentData.description,
                    date: incidentData.date,
                    resolved: incidentData.resolved,
                    hebergement: incidentData.hebergement || ''
                });

                setLocalResolved(incidentData.resolved);
            }
        }
    }, [incidentData, authState.user]);

    // Fonction pour obtenir le style de l'indicateur de statut (rouge ou vert)
    const getStatusIndicator = (resolved: boolean) => ({
        backgroundColor: resolved ? 'green' : 'red',
        width: '15px',
        height: '15px',
        borderRadius: '50%',
        display: 'inline-block',
        margin: 'auto',
        marginLeft: '8px'
    })

    if (incidentLoading) {
        return <Loader />;
    }

    if (incidentError || !incidentData) {
        return <div>Error occurred while fetching the animation details.</div>;
    }

    const handleResolvedChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newResolved = e.target.value === 'true'; // Convertir en booléen
        setLocalResolved(newResolved); // Mettre à jour l'état local
    }

    const onSubmit  = async (formData: IncidentUpdateFormData) => {
        
        console.log(formData)
        const result = await updateIncident(formData)
        console.log(result)
        navigate('/follow-incidents')
    };

    return (
    <div className={IncidentStyle.IncidentContainer}>
        <PageHeader backTo='/follow-incidents' title={t('suivre_incident')} subtitle={t('suivre_incident_sub')} />

        <form onSubmit={handleSubmit(onSubmit)} className={IncidentStyle.ModifyLesseeForm}>
                <div className={`${IncidentStyle.ModifyLesseeFormChild}`}>
                    <div className={IncidentStyle.DesktopFormFieldHalf}>
                        <div className={`${IncidentStyle.half} ${IncidentStyle.InputContainer}`}>
                            <div className={IncidentStyle.Label}>{t('ref')}</div>
                            <div className={IncidentStyle.Data} id='référence'>{IncidentDetails.id}</div>
                        </div>
                        <div className={`${IncidentStyle.half} ${IncidentStyle.InputContainer}`}>
                            <div className={IncidentStyle.Label}>{t('creation_date')}</div>
                            <div className={IncidentStyle.Data} id='date'>{IncidentDetails.date}</div>
                        </div>
                    </div>
                    <div className={IncidentStyle.Status}>{t('status')}</div>
                    <div className={IncidentStyle.SelectContainer}>
                    <div className="status-indicator" style={getStatusIndicator(localResolved)}></div>
                        <select
                            id='resolved'
                            {...register('resolved', { required: true,
                              //setValueAs: v => v === 'true' // Conversion en booléen
                             })}
                             onChange={handleResolvedChange}
                            required
                            disabled={incidentData.resolved}
                        >
                            <option value="false">{t('en_cours')}</option>
                            <option value="true">{t('cloture')}</option>
                        </select>
                    </div>
                    <div className={IncidentStyle.DesktopFormField}>
                        <div className={IncidentStyle.InputContainer}>
                            <div className={IncidentStyle.Label}>{t('category')}</div>
                            <div className={IncidentStyle.Data} id='category'>{typeMapping[IncidentDetails.category]}</div>
                        </div>
                        <div className={IncidentStyle.InputContainer}>
                            <div className={IncidentStyle.Label}>{t('incident_title_short')}</div>
                            <div className={IncidentStyle.Data} id='title'>{IncidentDetails.title}</div>
                        </div>
                    </div>
                    {IncidentDetails.hebergement && (
                        <div className={IncidentStyle.DesktopFormField}>
                            <div className={IncidentStyle.InputContainer}>
                                <div className={IncidentStyle.Label}>{t('hebergement_concern')}</div>
                                <div className={IncidentStyle.Data} id='hebergement'>{IncidentDetails.hebergement}</div>
                            </div>
                        </div>
                    )}
                    <div className={IncidentStyle.DesktopDateField}>
                        <div className={IncidentStyle.InputContainer}>
                            <div className={IncidentStyle.Label}>{t('incident_precision_short')}*</div>
                            <div className={IncidentStyle.Data} id='birthdate'>{IncidentDetails.description}</div>
                        </div>
                    </div>
                </div>
                {!incidentData.resolved && (
                <div className={IncidentStyle.buttonContainer}>
                    <button className={IncidentStyle.Update} type='submit' disabled={isLoading}>
                        {isLoading ? t('loading') + '...' : t('send')}
                    </button>
                </div>
                )}
            </form>
        </div>
    );
};

export default IncidentDetailPage;
